import { useQueryClient } from '@tanstack/react-query';
import { supplierSchema, type SupplierDTO } from '@bifeai/shared';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { Archive, Ellipsis, HandCoins, Mail, MapPin, Pencil, Phone, Plus, ReceiptText, Truck, UserRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { SearchBox, Toolbar, ViewToggle } from '../components/inventory';
import { api, errorMessage, isApiError } from '../lib/api';
import { invalidateInventory, useSuppliers, zodFieldErrors } from '../lib/catalog';
import { formatDate, formatMoney } from '../lib/format';
import { useSession } from '../lib/session';
import { SwitchRow } from '../components/SwitchRow';
import { useViewMode } from '../lib/view-mode';

const termsLabel = (days: number) => (days === 0 ? 'Contado' : `${days} días`);
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export function SuppliersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { can, isHeadquartersUser } = useSession();
  const [view, setView] = useViewMode('proveedores');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SupplierDTO | 'new' | null>(null);
  const [archiving, setArchiving] = useState<SupplierDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const suppliers = useSuppliers();

  const canManage = can('suppliers.manage') || can('purchases.manage');
  const canArchive = can('suppliers.manage');
  const canBuy = can('purchases.manage');
  const canPay = can('suppliers.manage');
  // Una sucursal no edita proveedores de toda la cadena.
  const editable = (s: SupplierDTO) => canManage && !s.isBrand && (isHeadquartersUser || s.branchId !== null);

  const q = norm(search.trim());
  const list = (suppliers.data ?? []).filter((s) => !q || norm(`${s.name} ${s.cuit ?? ''} ${s.contactName ?? ''}`).includes(q));
  const totalBalance = (suppliers.data ?? []).reduce((a, s) => a + s.balanceCents, 0);

  async function archive(s: SupplierDTO) {
    setBusy(true);
    try {
      await api.del(`/suppliers/${s.id}`);
      toast.success('Proveedor archivado', s.name);
      setArchiving(null);
      await invalidateInventory(qc);
    } catch (err) {
      toast.error('No se pudo archivar', errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const menu = (s: SupplierDTO) =>
    (editable(s) || canBuy || canPay) && (
      <Menu>
        <MenuTrigger asChild>
          <button className="bf-icon-btn" aria-label={`Acciones para ${s.name}`}>
            <Ellipsis />
          </button>
        </MenuTrigger>
        <MenuContent>
          {canPay && (
            <MenuItem icon={<HandCoins />} onSelect={() => navigate(`/proveedores/${s.id}`)}>
              Cuenta y pagos
            </MenuItem>
          )}
          {canBuy && !s.isBrand && (
            <MenuItem icon={<ReceiptText />} onSelect={() => navigate(`/compras/nueva?proveedor=${s.id}`)}>
              Cargar compra
            </MenuItem>
          )}
          {canBuy && (
            <MenuItem icon={<Truck />} onSelect={() => navigate(`/compras?proveedor=${s.id}`)}>
              Ver compras
            </MenuItem>
          )}
          {editable(s) && (
            <MenuItem icon={<Pencil />} onSelect={() => setEditing(s)}>
              Editar datos
            </MenuItem>
          )}
          {editable(s) && canArchive && (
            <MenuItem icon={<Archive />} danger onSelect={() => setArchiving(s)}>
              Archivar
            </MenuItem>
          )}
        </MenuContent>
      </Menu>
    );

  return (
    <>
      <PageHeader
        title="Proveedores"
        subtitle={
          totalBalance > 0 ? `Saldo total a pagar: ${formatMoney(totalBalance)}.` : 'Frigoríficos, granjas y distribuidores a los que les comprás.'
        }
        actions={
          <>
            {canPay && (
              <Button icon={<HandCoins />} onClick={() => navigate('/proveedores/pagos')}>
                Pagos y vencimientos
              </Button>
            )}
            {canManage && (
              <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
                Nuevo proveedor
              </Button>
            )}
          </>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Buscar por nombre, CUIT o contacto" />
        <span className="bf-toolbar-spacer" />
        <ViewToggle value={view} onChange={setView} />
      </Toolbar>

      {suppliers.isPending ? (
        <div className="bf-entity-grid">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} style={{ height: 220, borderRadius: 14 }} />
          ))}
        </div>
      ) : list.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Truck />}
            title={q ? 'No encontramos proveedores' : 'Todavía no hay proveedores'}
            text={q ? 'Probá con otra búsqueda.' : 'Cargalos para registrar compras, lotes y lo que les debés.'}
            action={
              !q &&
              canManage && (
                <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
                  Nuevo proveedor
                </Button>
              )
            }
          />
        </Card>
      ) : view === 'grid' ? (
        <div className="bf-entity-grid">
          {list.map((s) => (
            <article key={s.id} className="bf-entity-card">
              <div className="bf-entity-head">
                <Avatar name={s.name} />
                <div>
                  <div className="bf-entity-name">{s.name}</div>
                  <div className="bf-cell-sub">
                    {s.cuit ? `CUIT ${s.cuit}` : 'Sin CUIT'} · Pago {termsLabel(s.paymentTermsDays).toLowerCase()}
                  </div>
                </div>
                {menu(s)}
              </div>
              <div className="bf-entity-lines">
                {s.contactName && (
                  <span>
                    <UserRound /> {s.contactName}
                  </span>
                )}
                {s.phone && (
                  <a href={`tel:${s.phone.replace(/[^\d+]/g, '')}`}>
                    <Phone /> {s.phone}
                  </a>
                )}
                {s.email && (
                  <a href={`mailto:${s.email}`}>
                    <Mail /> {s.email}
                  </a>
                )}
                {s.address && (
                  <span>
                    <MapPin /> {s.address}
                  </span>
                )}
                {!s.contactName && !s.phone && !s.email && !s.address && <span className="bf-text-muted">Sin datos de contacto</span>}
              </div>
              {isHeadquartersUser && s.branchName && (
                <div>
                  <Badge tone="info">Solo {s.branchName}</Badge>
                </div>
              )}
              <div className="bf-entity-stats">
                <div>
                  <div className="bf-stat-label">Saldo a pagar</div>
                  <div className="bf-stat-value" data-tone={s.balanceCents > 0 ? 'warning' : undefined}>
                    {formatMoney(s.balanceCents)}
                  </div>
                </div>
                <div>
                  <div className="bf-stat-label">Comprado este mes</div>
                  <div className="bf-stat-value">{formatMoney(s.purchasedThisMonthCents)}</div>
                </div>
              </div>
              <div className="bf-cell-sub">{s.lastPurchaseAt ? `Última compra: ${formatDate(s.lastPurchaseAt)}` : 'Sin compras todavía'}</div>
            </article>
          ))}
        </div>
      ) : (
        <Card>
          <CardBody flush>
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Proveedor</th>
                    <th>Contacto</th>
                    <th>Pago</th>
                    <th>Este mes</th>
                    <th>Saldo a pagar</th>
                    <th>Última compra</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <span className="bf-cell-main">{s.name}</span>{' '}
                        {isHeadquartersUser && s.branchName && <Badge tone="info">{s.branchName}</Badge>}
                        {s.approvedForFranchises && <Badge tone="success">Homologado</Badge>}
                        {s.isBrand && <Badge tone="info">La marca · se carga sola</Badge>}
                        <div className="bf-cell-sub">{s.cuit ? `CUIT ${s.cuit}` : 'Sin CUIT'}</div>
                      </td>
                      <td>
                        {s.contactName ?? '—'}
                        <div className="bf-cell-sub">{[s.phone, s.email].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td>{termsLabel(s.paymentTermsDays)}</td>
                      <td className="bf-num">{formatMoney(s.purchasedThisMonthCents)}</td>
                      <td className={`bf-num ${s.balanceCents > 0 ? 'bf-text-warning' : ''}`}>{formatMoney(s.balanceCents)}</td>
                      <td className="bf-cell-sub">{s.lastPurchaseAt ? formatDate(s.lastPurchaseAt) : '—'}</td>
                      <td className="bf-cell-actions">{menu(s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {editing && (
        <SupplierDialog
          supplier={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (s, isNew) => {
            toast.success(isNew ? 'Proveedor creado' : 'Proveedor actualizado', s.name);
            setEditing(null);
            await invalidateInventory(qc);
          }}
        />
      )}

      <Dialog
        open={Boolean(archiving)}
        onOpenChange={(o) => !o && setArchiving(null)}
        title="Archivar proveedor"
        description={`${archiving?.name ?? ''} deja de aparecer para cargar compras. Sus compras y pagos quedan en el historial.`}
      >
        <div className="bf-form-actions">
          <Button onClick={() => setArchiving(null)}>Cancelar</Button>
          <Button variant="danger" icon={<Archive />} loading={busy} onClick={() => archiving && void archive(archiving)}>
            Archivar
          </Button>
        </div>
      </Dialog>
    </>
  );
}

export function SupplierDialog({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: SupplierDTO | null;
  onClose: () => void;
  onSaved: (s: SupplierDTO, isNew: boolean) => Promise<void>;
}) {
  const { me, isHeadquartersUser, multiBranch, franchisesEnabled } = useSession();
  const [approved, setApproved] = useState(supplier?.approvedForFranchises ?? false);
  const [form, setForm] = useState({
    name: supplier?.name ?? '',
    cuit: supplier?.cuit ?? '',
    contactName: supplier?.contactName ?? '',
    phone: supplier?.phone ?? '',
    email: supplier?.email ?? '',
    address: supplier?.address ?? '',
    paymentTermsDays: String(supplier?.paymentTermsDays ?? 0),
    notes: supplier?.notes ?? '',
    branchId: supplier?.branchId ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const parsed = supplierSchema.safeParse({
      ...form,
      paymentTermsDays: Number(form.paymentTermsDays || 0),
      // Una sucursal crea proveedores propios; la API lo fuerza igual.
      branchId: isHeadquartersUser ? form.branchId || null : me.membership.branchId,
      approvedForFranchises: isHeadquartersUser && !form.branchId && approved,
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    try {
      const saved = supplier
        ? await api.patch<SupplierDTO>(`/suppliers/${supplier.id}`, parsed.data)
        : await api.post<SupplierDTO>('/suppliers', parsed.data);
      await onSaved(saved, !supplier);
    } catch (err) {
      if (isApiError(err) && err.fields) setErrors(err.fields);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()} title={supplier ? 'Editar proveedor' : 'Nuevo proveedor'} wide>
      <form className="bf-form" onSubmit={submit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <div className="bf-form-row">
          <Field label="Nombre o razón social" error={errors.name}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Frigorífico del Sur S.A." autoFocus />
          </Field>
          <Field label="CUIT" error={errors.cuit}>
            <Input inputMode="numeric" value={form.cuit} onChange={(e) => set('cuit', e.target.value)} placeholder="30-12345678-9" />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Persona de contacto" error={errors.contactName}>
            <Input value={form.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="Opcional" />
          </Field>
          <Field label="Teléfono / WhatsApp" error={errors.phone}>
            <Input inputMode="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="11 5555-0000" />
          </Field>
          <Field label="Email" error={errors.email}>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="Opcional" />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Dirección" error={errors.address}>
            <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Opcional" />
          </Field>
          <Field label="Plazo de pago" error={errors.paymentTermsDays} hint="Con esto calculamos el vencimiento de cada compra.">
            <Select value={form.paymentTermsDays} onChange={(e) => set('paymentTermsDays', e.target.value)}>
              {[0, 7, 10, 15, 21, 30, 45, 60, 90].map((d) => (
                <option key={d} value={d}>
                  {termsLabel(d)}
                </option>
              ))}
            </Select>
          </Field>
          {isHeadquartersUser && multiBranch && (
            <Field label="Le compra" error={errors.branchId}>
              <Select value={form.branchId} onChange={(e) => set('branchId', e.target.value)}>
                <option value="">{franchisesEnabled ? 'Locales propios de la marca' : 'Toda la cadena'}</option>
                {me.branches
                  .filter((b) => !b.franchise)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      Solo {b.name}
                    </option>
                  ))}
              </Select>
            </Field>
          )}
        </div>
        {franchisesEnabled && isHeadquartersUser && !form.branchId && (
          <SwitchRow
            label="Homologado para las franquicias"
            hint="Las franquicias que pueden comprarle a proveedores homologados lo ven y le compran directo. El resto de tus proveedores no lo ven."
            checked={approved}
            onChange={setApproved}
          />
        )}
        <Field label="Notas" error={errors.notes}>
          <Textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Días de entrega, condiciones, etc." />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            {supplier ? 'Guardar cambios' : 'Crear proveedor'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
